import {
  IBasemapInput,
  IDeliveryMaterial,
  IGlobeViewInput,
  IGlobeViewStyleInput,
  IMutation,
  IOperationRuleInput,
  IProject,
  IRemoteTileset,
  ISitemapInput,
  ISitemapItemInput,
  ISitemapSpecificItemDataCircleInput,
  ISitemapSpecificItemDataPolylineInput,
  ISitemapSpecificItemDataRectangleInput,
  IUpdateDeliveriesFieldsInput,
  IWorkflowLocationRestriction,
} from '~/client/graph'
import { SaveAnalyticsSettingDocument } from '~/client/graph/operations/generated/AnalyticsSettings.generated'
import { SaveBasemapDocument } from '~/client/graph/operations/generated/Basemaps.generated'
import {
  ChangeDeliveriesStatusDocument,
  UpdateDeliveriesFieldsDocument,
  UpdateDeliveryDocument,
} from '~/client/graph/operations/generated/Deliveries.generated'
import {
  DeleteGlobeViewDocument,
  DeleteGlobeViewSpecificItemDataDocument,
  SaveGlobeViewDocument,
  SaveGlobeViewSpecificItemDataDocument,
  SaveGlobeViewStyleDocument,
} from '~/client/graph/operations/generated/GlobeViews.generated'
import {
  DeleteManyWorkflowLocationRestrictionsDocument,
  SaveManyWorkflowLocationRestrictionsDocument,
} from '~/client/graph/operations/generated/LocationType.generated'
import { FillProjectWithDefaultMaterialsDocument } from '~/client/graph/operations/generated/Materials.generated'
import {
  DeleteManyOperationRulesDocument,
  SaveManyOperationRulesDocument,
} from '~/client/graph/operations/generated/OperationRules.generated'
import { DeletePermitTypesDocument } from '~/client/graph/operations/generated/PermitTypes.generated'
import { SavePresentationSettingsDocument } from '~/client/graph/operations/generated/PresentationSettings.generated'
import {
  SaveProjectsDocument,
  UpdateProjectIsLogoWhiteDocument,
} from '~/client/graph/operations/generated/Project.generated'
import { SaveProjectAddressesDocument } from '~/client/graph/operations/generated/ProjectAddress.generated'
import { SaveProjectColoringOptionsDocument } from '~/client/graph/operations/generated/ProjectColoringOptions.generated'
import { SaveProjectMaterialOptionsDocument } from '~/client/graph/operations/generated/ProjectMaterialOptions.generated'
import { SaveProjectStatusUpdateOptionsDocument } from '~/client/graph/operations/generated/ProjectStatusUpdateOptions.generated'
import { SaveProjectTypeOptionsDocument } from '~/client/graph/operations/generated/ProjectTypeOptions.generated'
import {
  DeleteRemoteTilesetsDocument,
  SaveRemoteTilesetDocument,
} from '~/client/graph/operations/generated/RemoteTilesets.generated'
import {
  DeleteManySitemapItemsDocument,
  SaveSitemapItemDocument,
} from '~/client/graph/operations/generated/SitemapItems.generated'
import {
  DeleteSitemapDocument,
  SaveSitemapDocument,
  SaveSitemapSpecificItemDataDocument,
} from '~/client/graph/operations/generated/Sitemaps.generated'
import { DeleteTeamDocument } from '~/client/graph/operations/generated/Teams.generated'
import { DeleteUserRoleDocument } from '~/client/graph/operations/generated/UserRoles.generated'
import { UploadUsersFromCsvDocument } from '~/client/graph/operations/generated/Users.generated'
import { SaveZoneMapThresholdsDocument } from '~/client/graph/operations/generated/ZoneMapThresholds.generated'
import DesktopRootStore from '~/client/src/desktop/stores/DesktopRoot.store'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { DeliveryPageMode } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { isProjectBasedPath } from '~/client/src/shared/constants/commonRoutes'
import DeliveryStatus from '~/client/src/shared/constants/DeliveryStatus'
import Delivery from '~/client/src/shared/models/Delivery'
import Project from '~/client/src/shared/models/Project'
import { handleEventCallback } from '~/client/src/shared/stores/EventStore/commonEvents'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import InitialState from '~/client/src/shared/stores/InitialState'
import { isEmptyObj } from '~/client/src/shared/utils/util'

import ISaveProjectSuccessOptions from '../../interfaces/ISaveProjectSuccessOptions'

export default function (
  eventsStore: DesktopEventStore,
  rootStore: DesktopRootStore,
) {
  eventsStore.on(
    e.SAVE_BASEMAP,
    (state, basemap: IBasemapInput, callbackFn?: (id: string) => void) => {
      state.loading.set(e.SAVE_BASEMAP, true)

      return {
        graphMutation: {
          mutation: SaveBasemapDocument,
          variables: {
            basemap,
          },
          onSuccess: [e.SAVE_BASEMAP_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.SAVE_BASEMAP],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_BASEMAP_SUCCESS,
    (state, callbackFn: (id: string) => void, { saveBasemap }: IMutation) => {
      state.loading.set(e.SAVE_BASEMAP, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveBasemap?.id],
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP,
    (state, sitemap: ISitemapInput, callbackFn?: (id: string) => void) => {
      state.loading.set(e.SAVE_SITEMAP, true)

      return {
        graphMutation: {
          mutation: SaveSitemapDocument,
          variables: {
            sitemap,
          },
          onSuccess: [e.SAVE_SITEMAP_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.SAVE_SITEMAP],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_GLOBE_VIEW,
    (state, id: string, callbackFn?: (id: string) => void) => {
      state.loading.set(e.DELETE_GLOBE_VIEW, true)

      return {
        graphMutation: {
          mutation: DeleteGlobeViewDocument,
          variables: {
            id,
          },
          onSuccess: [e.DELETE_GLOBE_VIEW_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.DELETE_GLOBE_VIEW],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_GLOBE_VIEW_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { deleteGlobeView }: IMutation,
    ) => {
      state.loading.set(e.DELETE_GLOBE_VIEW, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, deleteGlobeView],
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_STYLE,
    (
      state,
      style: IGlobeViewStyleInput,
      globeViewId: string,
      callbackFn?: (id: string) => void,
    ) => {
      state.loading.set(e.SAVE_GLOBE_VIEW_STYLE, true)

      return {
        graphMutation: {
          mutation: SaveGlobeViewStyleDocument,
          variables: {
            style,
            globeViewId,
          },
          onSuccess: [e.SAVE_GLOBE_VIEW_STYLE_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.SAVE_GLOBE_VIEW_STYLE],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_STYLE_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { saveGlobeViewStyle }: IMutation,
    ) => {
      state.loading.set(e.SAVE_GLOBE_VIEW_STYLE, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveGlobeViewStyle?.id],
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW,
    (state, globeView: IGlobeViewInput, callbackFn?: (id: string) => void) => {
      state.loading.set(e.SAVE_GLOBE_VIEW, true)

      return {
        graphMutation: {
          mutation: SaveGlobeViewDocument,
          variables: {
            globeView,
          },
          onSuccess: [e.SAVE_GLOBE_VIEW_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.SAVE_GLOBE_VIEW],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_SUCCESS,
    (state, callbackFn: (id: string) => void, { saveGlobeView }: IMutation) => {
      state.loading.set(e.SAVE_GLOBE_VIEW, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveGlobeView?.id],
      }
    },
  )

  eventsStore.on(
    e.DELETE_GLOBE_VIEW_ITEM_DATA,
    (state, id: string, callbackFn?: (id: string) => void) => {
      state.loading.set(e.DELETE_GLOBE_VIEW_ITEM_DATA_SUCCESS, true)

      return {
        graphMutation: {
          mutation: DeleteGlobeViewSpecificItemDataDocument,
          variables: {
            id,
          },
          onSuccess: [e.DELETE_GLOBE_VIEW_ITEM_DATA_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.DELETE_GLOBE_VIEW_ITEM_DATA],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_GLOBE_VIEW_ITEM_DATA_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { deleteGlobeViewSpecificItemData }: IMutation,
    ) => {
      state.loading.set(e.DELETE_GLOBE_VIEW_ITEM_DATA_SUCCESS, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, deleteGlobeViewSpecificItemData],
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_ITEM_DATA,
    (
      state,
      polylines: ISitemapSpecificItemDataPolylineInput,
      rectangles: ISitemapSpecificItemDataRectangleInput,
      circles: ISitemapSpecificItemDataCircleInput,
      callbackFn?: (id: string) => void,
      errorCallbackFn?: (error: unknown) => void,
    ) => {
      state.loading.set(e.SAVE_GLOBE_VIEW_ITEM_DATA, true)

      return {
        graphMutation: {
          mutation: SaveGlobeViewSpecificItemDataDocument,
          variables: {
            polylines,
            rectangles,
            circles,
          },
          onSuccess: [e.SAVE_GLOBE_VIEW_ITEM_DATA_SUCCESS, callbackFn as any],
          onError: [
            e.SAVE_GLOBE_VIEW_ITEM_DATA_FAILURE,
            errorCallbackFn as any,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_ITEM_DATA_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { saveGlobeViewSpecificItemData }: IMutation,
    ) => {
      state.loading.set(e.SAVE_GLOBE_VIEW_ITEM_DATA, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveGlobeViewSpecificItemData],
      }
    },
  )

  eventsStore.on(
    e.SAVE_GLOBE_VIEW_ITEM_DATA_FAILURE,
    (state, errorCallbackFn: (error: unknown) => void, error: unknown) => {
      state.loading.set(e.SAVE_GLOBE_VIEW_ITEM_DATA, false)
      // Route through REQUEST_ERROR for Sentry reporting and also run the
      // per-call error callback so awaiting callers can reject and react.
      return {
        dispatchN: [
          [e.REQUEST_ERROR, e.SAVE_GLOBE_VIEW_ITEM_DATA, error],
          [e.RUN_CALLBACK, errorCallbackFn, error],
        ],
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP_ITEM_DATA,
    (
      state,
      polylines: ISitemapSpecificItemDataPolylineInput,
      rectangles: ISitemapSpecificItemDataRectangleInput,
      circles: ISitemapSpecificItemDataCircleInput,
      callbackFn?: (id: string) => void,
    ) => {
      state.loading.set(e.SAVE_SITEMAP_ITEM_DATA, true)

      return {
        graphMutation: {
          mutation: SaveSitemapSpecificItemDataDocument,
          variables: {
            polylines,
            rectangles,
            circles,
          },
          onSuccess: [e.SAVE_SITEMAP_ITEM_DATA_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.SAVE_SITEMAP_ITEM_DATA],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP_ITEM_DATA_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { saveSitemapSpecificItemData }: IMutation,
    ) => {
      state.loading.set(e.SAVE_SITEMAP_ITEM_DATA_SUCCESS, false)
      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveSitemapSpecificItemData],
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP_SUCCESS,
    (state, callbackFn: (id: string) => void, { saveSitemap }: IMutation) => {
      state.loading.set(e.SAVE_SITEMAP, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveSitemap?.id],
      }
    },
  )

  eventsStore.on(
    e.DELETE_SITEMAP,
    (state, id: string, callbackFn?: () => void) => {
      state.loading.set(e.DELETE_SITEMAP, true)

      return {
        graphMutation: {
          mutation: DeleteSitemapDocument,
          variables: { id },
          onSuccess: [e.DELETE_SITEMAP_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.DELETE_SITEMAP],
        },
      }
    },
  )

  eventsStore.on(e.DELETE_SITEMAP_SUCCESS, (state, callback?: () => void) => {
    state.loading.set(e.DELETE_SITEMAP, false)

    return {
      dispatch: [e.RUN_CALLBACK, callback],
    }
  })

  eventsStore.on(
    e.SAVE_SITEMAP_ITEM,
    (
      state,
      sitemapItem: ISitemapItemInput,
      callbackFn?: (id: string) => void,
      errorCallbackFn?: (error: unknown) => void,
    ) => {
      state.loading.set(e.SAVE_SITEMAP_ITEM, true)

      return {
        graphMutation: {
          mutation: SaveSitemapItemDocument,
          variables: {
            sitemapItem,
          },
          onSuccess: [e.SAVE_SITEMAP_ITEM_SUCCESS, callbackFn as any],
          onError: [e.SAVE_SITEMAP_ITEM_FAILURE, errorCallbackFn as any],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP_ITEM_SUCCESS,
    (
      state,
      callbackFn: (id: string) => void,
      { saveSitemapItem }: IMutation,
    ) => {
      state.loading.set(e.SAVE_SITEMAP_ITEM, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn, saveSitemapItem?.id],
      }
    },
  )

  eventsStore.on(
    e.SAVE_SITEMAP_ITEM_FAILURE,
    (state, errorCallbackFn: (error: unknown) => void, error: unknown) => {
      state.loading.set(e.SAVE_SITEMAP_ITEM, false)
      return {
        dispatchN: [
          [e.REQUEST_ERROR, e.SAVE_SITEMAP_ITEM, error],
          [e.RUN_CALLBACK, errorCallbackFn, error],
        ],
      }
    },
  )

  eventsStore.on(
    e.DELETE_SITEMAP_ITEMS,
    (state, ids: string[], callbackFn?: () => void) => {
      return {
        graphMutation: {
          mutation: DeleteManySitemapItemsDocument,
          variables: { ids },
          onSuccess: [e.RUN_CALLBACK, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.DELETE_SITEMAP_ITEMS],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_MATERIAL_OPTIONS,
    (state, options, onSuccessCb, onErrorCb) => {
      const projectMaterialOptions = options || state.projectMaterialOptions

      return {
        graphMutation: {
          mutation: SaveProjectMaterialOptionsDocument,
          variables: { projectMaterialOptions },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_ADDRESSES,
    (
      state,
      projectAddresses,
      onSuccessCb,
      onErrorCb,
      shouldCompleteRequest,
    ) => {
      state.loading.set(e.SAVE_PROJECT_ADDRESSES, true)
      return {
        graphMutation: {
          mutation: SaveProjectAddressesDocument,
          variables: { projectAddresses },
          onSuccess: handleEventCallback(
            e.SAVE_PROJECT_ADDRESSES,
            shouldCompleteRequest,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.SAVE_PROJECT_ADDRESSES,
            shouldCompleteRequest,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_TYPE_OPTIONS,
    (
      state,
      projectTypeOptions,
      onSuccessCb,
      onErrorCb,
      shouldCompleteRequest,
    ) => {
      state.loading.set(e.SAVE_PROJECT_TYPE_OPTIONS, true)
      return {
        graphMutation: {
          mutation: SaveProjectTypeOptionsDocument,
          variables: { projectTypeOptions },
          onSuccess: handleEventCallback(
            e.SAVE_PROJECT_TYPE_OPTIONS,
            shouldCompleteRequest,
            onSuccessCb,
          ),
          onError: handleEventCallback(
            e.SAVE_PROJECT_TYPE_OPTIONS,
            shouldCompleteRequest,
            onErrorCb,
          ),
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_SU_OPTIONS,
    (state, projectStatusUpdateOptions, onSuccessCb, onErrorCb) => {
      return {
        graphMutation: {
          mutation: SaveProjectStatusUpdateOptionsDocument,
          variables: { projectStatusUpdateOptions },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_COLORING_OPTIONS,
    (state, projectColoringOptions, onSuccessCb, onErrorCb) => {
      return {
        graphMutation: {
          mutation: SaveProjectColoringOptionsDocument,
          variables: { projectColoringOptions },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT,
    (state, projectToSave: IProject | Project, successOptions, onErrorCb) => {
      state.loading.set(e.SAVE_PROJECT, true)

      const project = Project.toInput(projectToSave)

      return {
        graphMutation: {
          mutation: SaveProjectsDocument,
          variables: { projects: [project] },
          onSuccess: [e.SAVE_PROJECT_SUCCESS, successOptions as any],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PROJECT_SUCCESS,
    (
      state,
      successOptions: ISaveProjectSuccessOptions,
      { saveManyProjects }: IMutation,
    ) => {
      state.loading.set(e.SAVE_PROJECT, false)

      const { onSuccessCb, hasTimezoneChanged } = successOptions

      const [savedProject] = saveManyProjects

      if (hasTimezoneChanged) {
        window.location.reload()
      }

      const { activeProject } = state
      if (activeProject.areIdsEquals(savedProject)) {
        activeProject.updateFromJson(savedProject)
        rootStore.projectsStore.setOne(activeProject)
      }

      return { dispatch: [e.RUN_CALLBACK, onSuccessCb, savedProject.id] }
    },
  )

  eventsStore.on(
    e.UPDATE_PROJECT_IS_LOGO_WHITE,
    (state, isLogoWhite: boolean, onErrorCb) => {
      state.activeProject.isLogoWhite = isLogoWhite
      return {
        graphMutation: {
          mutation: UpdateProjectIsLogoWhiteDocument,
          variables: {
            projectId: state.activeProject.id,
            isLogoWhite,
          },
          onSuccess: [e.NO_EFFECT],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.UPLOAD_PROJECT_MEMBERS,
    (state, fileContent, reassignClaimedPhone, onSuccess, onError) => {
      if (!fileContent) {
        return
      }

      state.loading.set(e.UPLOAD_PROJECT_MEMBERS, true)

      return {
        graphMutation: {
          mutation: UploadUsersFromCsvDocument,
          variables: {
            uploadUsersFromCsv: {
              file: fileContent,
              projectId: state.activeProject.id,
            },
            reassignClaimedPhone,
          },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.UPLOAD_PROJECT_MEMBERS,
            onSuccess,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.UPLOAD_PROJECT_MEMBERS,
            onError,
          ],
        },
      }
    },
  )

  eventsStore.on(e.CHANGE_DELIVERY_STATUS_SUCCESS, state => {
    state.delivery.compactView.highlightedDelivery = null
    state.delivery.compactView.mode = DeliveryPageMode.VIEW
    state.delivery.compactView.resetViewMode()
  })

  eventsStore.on(
    e.EDIT_BULK_DELIVERIES,
    (state, fields: IUpdateDeliveriesFieldsInput, onSuccessCb, onErrorCb) => {
      state.loading.set(e.EDIT_BULK_DELIVERIES, true)

      const timestamp = Date.now()
      state.timestampToEditingDeliveriesIdsMap[timestamp] = fields.deliveryIds
      fields.timezoneId = rootStore.projectDateStore.getClientTimezoneId()

      return {
        graphMutation: {
          mutation: UpdateDeliveriesFieldsDocument,
          variables: { fieldsData: fields },
          onSuccess: [e.EDIT_BULK_DELIVERIES_RESULT, onSuccessCb, timestamp],
          onError: [e.EDIT_BULK_DELIVERIES_RESULT, onErrorCb, timestamp],
        },
      }
    },
  )

  eventsStore.on(
    e.EDIT_BULK_DELIVERIES_RESULT,
    (state, callback, timestamp) => {
      if (callback) {
        callback()
      }

      delete state.timestampToEditingDeliveriesIdsMap[timestamp]

      if (isEmptyObj(state.timestampToEditingDeliveriesIdsMap)) {
        state.loading.set(e.EDIT_BULK_DELIVERIES, false)
      }
    },
  )

  eventsStore.on(
    e.UPDATE_BULK_DELIVERIES_STATUS,
    (
      state,
      deliveryIds: string[],
      nextStatus: DeliveryStatus,
      onSuccessCallback,
      onErrorCallback,
    ) => {
      state.loading.set(e.UPDATE_BULK_DELIVERIES_STATUS, true)

      const timestamp = Date.now()
      state.timestampToUpdatingDeliveriesIdsMap[timestamp] = deliveryIds

      return {
        graphMutation: {
          mutation: ChangeDeliveriesStatusDocument,
          variables: {
            statusData: {
              projectId: state.activeProject.id,
              deliveryIds,
              nextStatus,
            },
          },
          onSuccess: [
            e.UPDATE_BULK_DELIVERIES_STATUS_RESULT,
            onSuccessCallback,
            timestamp,
          ],
          onError: [
            e.UPDATE_BULK_DELIVERIES_STATUS_RESULT,
            onErrorCallback,
            timestamp,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.UPDATE_BULK_DELIVERIES_STATUS_RESULT,
    (state, callback, timestamp) => {
      const deliveriesIds = state.timestampToUpdatingDeliveriesIdsMap[timestamp]

      if (callback) {
        callback(deliveriesIds.length)
      }

      delete state.timestampToUpdatingDeliveriesIdsMap[timestamp]

      if (isEmptyObj(state.timestampToUpdatingDeliveriesIdsMap)) {
        state.loading.set(e.UPDATE_BULK_DELIVERIES_STATUS, false)
      }
    },
  )

  eventsStore.on(
    e.CHANGE_PASSWORD,
    (state, newPassword, currentPassword, onSuccessCb, onErrorCb) => {
      state.loading.set(e.CHANGE_PASSWORD, true)

      const { auth } = rootStore
      return {
        cmd: {
          cmd: {
            execute: () => {
              return auth.setPassword(newPassword, currentPassword)
            },
          },
          onSuccess: [e.CHANGE_PASSWORD_RESULT, onSuccessCb],
          onError: [e.CHANGE_PASSWORD_RESULT, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.CHANGE_PASSWORD_RESULT,
    (
      state,
      callback: (error?: Error & { code?: string }) => any,
      error?: Error & { code?: string },
    ) => {
      state.loading.set(e.CHANGE_PASSWORD, false)

      if (callback) {
        callback(error || null)
      }
    },
  )

  /**
   * Keep track of the active project and active schedule id
   */
  eventsStore.on(e.ACTIVATE_PROJECT_SUCCESS, (state, project: IProject) => {
    state.loading.set(e.ACTIVATE_PROJECT, false)
    state.activeProject = Project.fromDto(project)
    eventsStore.dispatch(e.INVALIDATE_LOCATION_LOAD)
    state.resetResourceDisplayedOnGlobalCard()
    state.resetWorkflowIdDisplayedOnGlobalCard()

    rootStore.clearOutProjectData()
    rootStore.clearAllFilters()
    rootStore.clearAllCustomFilters()

    eventsStore.terminateGraphSubscriptions()

    // Post-activation navigation. Mirrors mobile's DISPLAY_DEFAULT_MOBILE_PAGE
    // pattern (mobileEvents.ts ACTIVATE_PROJECT_SUCCESS): the event handler
    // decides where to land, the view-store executes the navigation. On a
    // project path (NavBar swap from /project/X/<view> to /project/Y/<view>),
    // keep the same view and rewrite the project segment. Otherwise (entry
    // from /projects picker, or any non-project route) hand off to the `/`
    // catch-all so redirectToDefaultRoute picks the right per-project landing
    // from `viewToDisplay` + `activeOrInitProjectCode`.
    if (isProjectBasedPath()) {
      rootStore.common.displaySameViewWithUpdatedProjectId()
    } else {
      rootStore.common.displayDefaultView()
    }

    return {
      flow: {
        startWith: [[e.RESET_INPUT_STATUSES], [e.GET_AUTH_USER_PROJECT]],
        rules: [
          {
            when: 'on',
            event: e.GET_AUTH_USER_PROJECT_SUCCESS,
            dispatch: [e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS],
          },
          {
            when: 'on',
            event: e.PROJECT_TYPE_OPTIONS_RECEIVED,
            dispatch: [e.INIT_APP_3],
            shouldTerminate: true,
          },
        ],
      },
    }
  })

  eventsStore.on(e.RESET_ALL_FILTERS, () => {
    rootStore.initAllFilters()
  })

  eventsStore.on(e.SET_FILTER_SELECTION, () => {
    // only postEventCallback is needed
  })

  eventsStore.on(e.RESET_FILTER, () => {
    // only postEventCallback is needed
  })

  eventsStore.on(e.SAVE_SITEMAP_IMAGE, () => {
    // only postEventCallback is needed
  })

  eventsStore.on(e.SAVE_GLOBE_IMAGE, () => {
    // only postEventCallback is needed
  })

  eventsStore.on(
    e.UPDATE_DELIVERY_MATERIALS,
    (
      state,
      delivery: Delivery,
      materials: IDeliveryMaterial[],
      onSuccessCallback,
      onErrorCallback,
    ) => {
      const { id: projectId } = state.activeProject

      return {
        graphMutation: {
          mutation: UpdateDeliveryDocument,
          variables: {
            delivery: {
              ...delivery.getDto(projectId),
              materials,
            },
            timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
          },
          onSuccess: [e.RUN_CALLBACK, onSuccessCallback],
          onError: [e.RUN_CALLBACK, onErrorCallback],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_PRESENTATION_SETTINGS,
    (state, settings, onSuccessCb, onErrorCb) => {
      const presentationSettings = Object.assign(
        state.presentationSettings,
        settings,
        {
          updatedBy: state.user.id,
          projectId: state.activeProject.id,
        },
      )

      return {
        graphMutation: {
          mutation: SavePresentationSettingsDocument,
          variables: { presentationSettings },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_ZONEMAP_THRESHOLDS,
    (state, thresholds, onSuccessCb, onErrorCb) => {
      const zoneMapThresholds = thresholds || state.zoneMapThresholds

      return {
        graphMutation: {
          mutation: SaveZoneMapThresholdsDocument,
          variables: { zoneMapThresholds },
          onSuccess: [e.RUN_CALLBACK, onSuccessCb],
          onError: [e.RUN_CALLBACK, onErrorCb],
        },
      }
    },
  )

  eventsStore.on(e.HANDLE_SAME_PROJECT_SELECTION, () => {
    // only mobile event handler
    return
  })

  eventsStore.on(e.CALCULATE_DELIVERY_TREE, () => {
    // only mobile event handler
    return
  })

  eventsStore.on(
    e.DELETE_PROJECT_TEAM,
    (state, teamId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_TEAM, true)

      return {
        graphMutation: {
          mutation: DeleteTeamDocument,
          variables: { teamId },
          onSuccess: [e.DELETE_PROJECT_TEAM_SUCCESS, teamId as any, callback],
          onError: [e.REQUEST_ERROR, e.DELETE_PROJECT_TEAM],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_PROJECT_TEAM_SUCCESS,
    (state, teamId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_TEAM, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, teamId],
      }
    },
  )

  eventsStore.on(
    e.DELETE_PROJECT_ROLE,
    (state, userRoleId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_ROLE, true)

      return {
        graphMutation: {
          mutation: DeleteUserRoleDocument,
          variables: { userRoleId },
          onSuccess: [
            e.DELETE_PROJECT_ROLE_SUCCESS,
            userRoleId as any,
            callback,
          ],
          onError: [e.REQUEST_ERROR, e.DELETE_PROJECT_ROLE],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_PROJECT_ROLE_SUCCESS,
    (state, roleId: string, callback?: (id: string) => void) => {
      state.loading.set(e.DELETE_PROJECT_ROLE, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, roleId],
      }
    },
  )

  eventsStore.on(
    e.REMOVE_WORKFLOW_TYPES,
    (state: InitialState, ids: string[]) => {
      state.loading.set(e.REMOVE_WORKFLOW_TYPES, true)

      if (!ids?.length) {
        return
      }

      return {
        graphMutation: {
          mutation: DeletePermitTypesDocument,
          variables: { ids },
          onSuccess: [e.REMOVE_WORKFLOW_TYPES_SUCCESS, ids],
          onError: [e.REQUEST_ERROR, e.REMOVE_WORKFLOW_TYPES],
        },
      }
    },
  )

  eventsStore.on(
    e.REMOVE_WORKFLOW_TYPES_SUCCESS,
    (
      state: InitialState,
      ids: string[],
      { softDeleteManyPermitTypes }: IMutation,
    ) => {
      state.loading.set(e.REMOVE_WORKFLOW_TYPES, false)

      if (softDeleteManyPermitTypes) {
        rootStore.workflowTypesStore.markTypesAsDeleted(ids)
      }
    },
  )

  eventsStore.on(
    e.SAVE_MANY_OPERATION_RULES,
    (
      state: InitialState,
      operationRules: IOperationRuleInput[],
      callbackFn?,
    ) => {
      state.loading.set(e.SAVE_MANY_OPERATION_RULES, true)

      return {
        graphMutation: {
          mutation: SaveManyOperationRulesDocument,
          variables: { operationRules },
          onSuccess: [e.SAVE_MANY_OPERATION_RULES_SUCCESS, callbackFn],
          onError: [e.REQUEST_ERROR, e.SAVE_MANY_OPERATION_RULES],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_MANY_OPERATION_RULES_SUCCESS,
    (
      state: InitialState,
      callbackFn,
      { saveManyOperationRules }: IMutation,
    ) => {
      state.loading.set(e.SAVE_MANY_OPERATION_RULES, false)

      saveManyOperationRules?.forEach(dto =>
        rootStore.operationRulesStore.receiveOne(dto.id, dto),
      )

      if (callbackFn) {
        callbackFn(saveManyOperationRules)
      }
    },
  )

  eventsStore.on(
    e.DELETE_MANY_OPERATION_RULES,
    (state, ids: string[], callbackFn?) => {
      state.loading.set(e.DELETE_MANY_OPERATION_RULES, true)

      return {
        graphMutation: {
          mutation: DeleteManyOperationRulesDocument,
          variables: { ids },
          onSuccess: [e.DELETE_MANY_OPERATION_RULES_SUCCESS, ids, callbackFn],
          onError: [e.REQUEST_ERROR, e.DELETE_MANY_OPERATION_RULES],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_MANY_OPERATION_RULES_SUCCESS,
    (
      state,
      ids: string[],
      callbackFn,
      { deleteManyOperationRules }: IMutation,
    ) => {
      state.loading.set(e.DELETE_MANY_OPERATION_RULES, false)

      if (deleteManyOperationRules) {
        ids.forEach(id => rootStore.operationRulesStore.receiveOne(id, null))
      }

      if (callbackFn) {
        callbackFn()
      }
    },
  )

  eventsStore.on(
    e.FILL_PROJECT_WITH_DEFAULT_MATERIALS,
    (
      state: InitialState,
      projectId: string,
      onSuccessCb: () => void,
      onErrorCb: () => void,
    ) => {
      state.loading.set(e.FILL_PROJECT_WITH_DEFAULT_MATERIALS, true)

      return {
        graphMutation: {
          mutation: FillProjectWithDefaultMaterialsDocument,
          variables: { projectId },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.FILL_PROJECT_WITH_DEFAULT_MATERIALS,
            onSuccessCb,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.FILL_PROJECT_WITH_DEFAULT_MATERIALS,
            onErrorCb,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_ANALYTICS_SETTING,
    (state: InitialState, analyticsSettings, callback) => {
      state.loading.set(e.SAVE_ANALYTICS_SETTING, true)
      return {
        graphMutation: {
          mutation: SaveAnalyticsSettingDocument,
          variables: {
            analyticsSettings,
          },
          onSuccess: [e.SAVE_ANALYTICS_SETTING_SUCCESS, callback],
          onError: [e.REQUEST_ERROR],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_ANALYTICS_SETTING_SUCCESS,
    (state, callback, { saveAnalyticsSettings }: IMutation) => {
      state.loading.set(e.SAVE_ANALYTICS_SETTING, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback, saveAnalyticsSettings],
      }
    },
  )

  eventsStore.on(
    e.DELETE_REMOTE_TILESET,
    (state: InitialState, id: string, callbackFn?: () => void) => {
      state.loading.set(e.DELETE_REMOTE_TILESET, true)
      return {
        graphMutation: {
          mutation: DeleteRemoteTilesetsDocument,
          variables: { ids: [id] },
          onSuccess: [e.DELETE_REMOTE_TILESETS_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.DELETE_REMOTE_TILESET],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_REMOTE_TILESETS_SUCCESS,
    (state, callback?: () => void) => {
      state.loading.set(e.DELETE_REMOTE_TILESET, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback],
      }
    },
  )

  eventsStore.on(
    e.UPLOAD_REMOTE_TILESETS,
    (
      state: InitialState,
      remoteTileset: IRemoteTileset,
      callbackFn?: () => void,
    ) => {
      state.loading.set(e.UPLOAD_REMOTE_TILESETS, true)

      return {
        graphMutation: {
          mutation: SaveRemoteTilesetDocument,
          variables: { remoteTileset },
          onSuccess: [e.UPLOAD_REMOTE_TILESETS_SUCCESS, callbackFn as any],
          onError: [e.REQUEST_ERROR, e.UPLOAD_REMOTE_TILESETS],
        },
      }
    },
  )

  eventsStore.on(
    e.UPLOAD_REMOTE_TILESETS_SUCCESS,
    (state, callback?: () => void) => {
      state.loading.set(e.UPLOAD_REMOTE_TILESETS, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback],
      }
    },
  )

  eventsStore.on(
    e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS,
    (state: InitialState, ids: string[], callbackFn?: () => void) => {
      state.loading.set(e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS, true)
      return {
        graphMutation: {
          mutation: DeleteManyWorkflowLocationRestrictionsDocument,
          variables: { ids },
          onSuccess: [
            e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS_SUCCESS,
            callbackFn as any,
          ],
          onError: [
            e.REQUEST_ERROR,
            e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS_SUCCESS,
    (state, callback?: () => void) => {
      state.loading.set(e.DELETE_MANY_WORKFLOW_LOCATION_RESTRICTIONS, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback],
      }
    },
  )

  eventsStore.on(
    e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS,
    (
      state: InitialState,
      workflowLocationRestrictions: IWorkflowLocationRestriction[],
      callbackFn?: () => void,
    ) => {
      state.loading.set(e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS, true)

      return {
        graphMutation: {
          mutation: SaveManyWorkflowLocationRestrictionsDocument,
          variables: { workflowLocationRestrictions },
          onSuccess: [
            e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS_SUCCESS,
            callbackFn as any,
          ],
          onError: [
            e.REQUEST_ERROR,
            e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS_SUCCESS,
    (state, callback?: () => void) => {
      state.loading.set(e.SAVE_MANY_WORKFLOW_LOCATION_RESTRICTIONS, false)

      return {
        dispatch: [e.RUN_CALLBACK, callback],
      }
    },
  )
}
